import React, { useState } from "react";
import type { ButtonProps } from "@mui/material";
import { Button, Grid } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import type { FieldValues, UseFormReturn } from "react-hook-form/dist/types";
import type { DefaultValues, ValidationMode } from "react-hook-form/dist/types/form";
import { ErrorAlert } from "../alert/ErrorAlert";
import { LoadingSubmitButton } from "./LoadingSubmitButton";

export type SubmittableFormProps<T extends FieldValues> = {
  submitAction: (data: T) => Promise<void> | void;
  children: (formController: UseFormReturn<T>) => React.ReactNode;
  defaultValues?: DefaultValues<T>;
  readOnly?: boolean;
  submitLabel?: string;
  submitColor?: ButtonProps["color"];
  onCancel?: () => void;
  cancelLabel?: string;
  mode?: keyof ValidationMode;
};

export function SubmittableForm<T extends FieldValues>(props: SubmittableFormProps<T>) {
  const { submitAction, children, defaultValues, readOnly, submitLabel, submitColor, onCancel, cancelLabel } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<unknown>(undefined);

  const formController = useForm<T>({ mode: props.mode || "onBlur", defaultValues });

  const onSubmit = async (data: T) => {
    try {
      setIsLoading(true);

      // The UnpackNestedValue type doesn't play well with Luxon's DateTime type so if we cast as the generic type everything works
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      await submitAction(data as T);
    } catch (ex) {
      setApiError(ex);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...formController}>
      <form onSubmit={formController.handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <>
            {!!apiError && <ErrorAlert error={apiError} />}
            {children(formController)}
            {!readOnly && (
              <Grid
                container
                size={12}
                sx={{
                  justifyContent: "flex-end",
                }}
              >
                {onCancel && (
                  <Button
                    onClick={onCancel}
                    variant={"text"}
                    color={"primary"}
                    sx={{ mr: 2, textTransform: "none" }}
                    disableElevation
                  >
                    {cancelLabel ?? "Cancel"}
                  </Button>
                )}
                <LoadingSubmitButton
                  isLoading={isLoading}
                  isDisabled={!formController.formState.isValid}
                  label={submitLabel}
                  color={submitColor}
                />
              </Grid>
            )}
          </>
        </Grid>
      </form>
    </FormProvider>
  );
}
